'use client';

import { c as _c } from "react/compiler-runtime";
import { Required } from '@/features/payload-cms/components/form/required';
import type { DateSlotSelectionBlock } from '@/features/payload-cms/components/form/types';
import type { CampPhase } from '@/features/payload-cms/components/form/utils/camp-phases';
import { findCampPhase, getCampPhasesWithin } from '@/features/payload-cms/components/form/utils/camp-phases';
import { formatDayCount, formatIsoDay, formatRange, formatRangeDates } from '@/features/payload-cms/components/form/utils/date-slot-format';
import type { DateRange, SelectableDays } from '@/features/payload-cms/components/form/utils/date-slots';
import { addDays, areRangesAllowed, getSelectableDays, parseDateRangesValue, toDateRangesValue, toIsoDay } from '@/features/payload-cms/components/form/utils/date-slots';
import { RESSORT_WISH_OPTIONS } from '@/features/payload-cms/constants/ressort-options';
import type { Locale, StaticTranslationString } from '@/types/types';
import { i18nConfig } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { CalendarX, X } from 'lucide-react';
import { useCurrentLocale } from 'next-i18n-router/client';
import React, { useMemo, useState } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
interface DateSlotSelectionProperties extends DateSlotSelectionBlock {
  control: Control<FieldValues>;
  renderMode?: 'all' | 'sidebar' | 'main';
}
const requiredFieldMessage: StaticTranslationString = {
  de: 'Dieses Feld ist erforderlich',
  en: 'This field is required',
  fr: 'Ce champ est obligatoire'
};
const noDaysText: StaticTranslationString = {
  de: 'Zurzeit können keine Tage ausgewählt werden.',
  en: 'No days can be selected at the moment.',
  fr: 'Aucun jour ne peut être sélectionné pour le moment.'
};
const pickFirstDayText: StaticTranslationString = {
  de: 'Tippe auf deinen ersten Tag.',
  en: 'Tap your first day.',
  fr: 'Touche ton premier jour.'
};
const pickLastDayText: StaticTranslationString = {
  de: 'Tippe jetzt auf deinen letzten Tag',
  en: 'Now tap your last day',
  fr: 'Touche maintenant ton dernier jour'
};
const atLeastText: StaticTranslationString = {
  de: 'mindestens',
  en: 'at least',
  fr: 'au moins'
};
const atMostText: StaticTranslationString = {
  de: 'höchstens',
  en: 'at most',
  fr: 'au plus'
};
const pickAgainText: StaticTranslationString = {
  de: 'Tippe auf einen Tag, um neu zu wählen.',
  en: 'Tap a day to choose again.',
  fr: 'Touche un jour pour recommencer.'
};
const addAnotherRangeText: StaticTranslationString = {
  de: 'Optional: Tippe auf den ersten Tag eines weiteren Zeitfensters.',
  en: 'Optional: tap the first day of another slot.',
  fr: "Facultatif : touche le premier jour d'un autre créneau."
};
const removeRangeText: StaticTranslationString = {
  de: 'Zeitfenster entfernen',
  en: 'Remove slot',
  fr: 'Retirer le créneau'
};
const ressortPlaceholder: StaticTranslationString = {
  de: 'Bitte auswählen',
  en: 'Please select',
  fr: 'Veuillez choisir'
};

/*
 * Appended to a day's accessible name, so a screen reader hears where the range starts and
 * ends instead of a row of independent "pressed" toggles.
 */
const firstDayMarker: StaticTranslationString = {
  de: 'erster Tag',
  en: 'first day',
  fr: 'premier jour'
};
const lastDayMarker: StaticTranslationString = {
  de: 'letzter Tag',
  en: 'last day',
  fr: 'dernier jour'
};
const inRangeMarker: StaticTranslationString = {
  de: 'ausgewählt',
  en: 'selected',
  fr: 'sélectionné'
};

/** Background of a phase's bar under the days and of its swatch in the legend. */
const phaseColor: Record<CampPhase['key'], string> = {
  setup: 'bg-amber-400',
  main: 'bg-red-700',
  teardown: 'bg-slate-400'
};

/** A Monday, used to print the weekday column headers in the reader's locale. */
const FIRST_MONDAY = Date.UTC(2024, 0, 1);
interface CalendarMonth {
  key: string;
  /** Empty cells before the first day, for a week that starts on Monday. */
  leadingBlanks: number;
  /** Every day of the month as `YYYY-MM-DD`. */
  days: string[];
}

/**
 * Every calendar month touched by the selectable window, oldest first. The window itself is
 * capped in `getSelectableDays`, so this never has to cut months off.
 */
const buildMonths = (firstDay: string, lastDay: string): CalendarMonth[] => {
  const [firstYear, firstMonth] = firstDay.split('-').map(Number);
  const [lastYear, lastMonth] = lastDay.split('-').map(Number);
  if (firstYear === undefined || firstMonth === undefined || lastYear === undefined || lastMonth === undefined) {
    return [];
  }
  const months: CalendarMonth[] = [];
  let year = firstYear;
  let month = firstMonth;
  while (year < lastYear || year === lastYear && month <= lastMonth) {
    const monthStart = Date.UTC(year, month - 1, 1);
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    months.push({
      key: `${year}-${month}`,
      leadingBlanks: (new Date(monthStart).getUTCDay() + 6) % 7,
      days: Array.from({
        length: daysInMonth
      }, (_, dayIndex) => toIsoDay(monthStart + dayIndex * 86_400_000))
    });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
};
interface DayRangeCalendarProperties {
  selectable: SelectableDays;
  value: unknown;
  onChange: (value: string) => void;
  label: string | undefined;
  locale: Locale;
}

/**
 * Month grids on which the helper taps a first and a last day, once per range.
 *
 * The form value only holds complete ranges; a first tap lives in local state until the
 * second one, so a half-picked range still fails the required check. While the helper may
 * add another range, a tap starts it next to the ones already marked. Once the limit is
 * reached, a tap starts over from scratch, which for a single range is plain "choose again".
 */
const DayRangeCalendar: React.FC<DayRangeCalendarProperties> = ({
  selectable,
  value,
  onChange,
  label,
  locale
}) => {
  const [pendingStart, setPendingStart] = useState<string | undefined>();
  const selectedRanges = parseDateRangesValue(value) ?? [];
  const isAtLimit = selectedRanges.length >= selectable.maxRanges;
  // The ranges a new one has to fit around: none when the next tap starts over.
  const keptRanges = pendingStart === undefined && isAtLimit ? [] : selectedRanges;
  const fitsBeside = (range: DateRange): boolean => areRangesAllowed([...keptRanges, range].sort((a, b) => a.startDate.localeCompare(b.startDate)), selectable);
  const findRange = (day: string): DateRange | undefined => selectedRanges.find(range_0 => day >= range_0.startDate && day <= range_0.endDate);
  const months = useMemo(() => buildMonths(selectable.firstDay, selectable.lastDay), [selectable.firstDay, selectable.lastDay]);
  const phases = useMemo(() => getCampPhasesWithin(selectable.firstDay, selectable.lastDay), [selectable.firstDay, selectable.lastDay]);
  const weekdays = useMemo(() => Array.from({
    length: 7
  }, (_, dayIndex) => formatIsoDay(toIsoDay(FIRST_MONDAY + dayIndex * 86_400_000), locale, {
    weekday: 'short'
  })), [locale]);

  /** A day can start a range when the shortest range from it still fits. */
  const canStartOn = (day_0: string): boolean => fitsBeside({
    startDate: day_0,
    endDate: addDays(day_0, selectable.minDays - 1)
  });
  const isSelectable = (day_1: string): boolean => {
    if (pendingStart === undefined || day_1 < pendingStart) return canStartOn(day_1);
    if (day_1 === pendingStart) return true;
    return fitsBeside({
      startDate: pendingStart,
      endDate: day_1
    });
  };
  const handleDayClick = (day_2: string): void => {
    if (pendingStart === undefined || day_2 < pendingStart) {
      if (pendingStart === undefined && isAtLimit) onChange('');
      setPendingStart(day_2);
    } else if (day_2 === pendingStart) {
      setPendingStart(undefined);
    } else {
      setPendingStart(undefined);
      onChange(toDateRangesValue([...selectedRanges, {
        startDate: pendingStart,
        endDate: day_2
      }]));
    }
  };
  const removeRange = (removed: DateRange): void => {
    setPendingStart(undefined);
    onChange(toDateRangesValue(selectedRanges.filter(range_1 => range_1 !== removed)));
  };

  /** What a day is within a range, for its accessible name; undefined when outside all. */
  const getDayMarker = (day_3: string): string | undefined => {
    const range_2 = findRange(day_3);
    if (day_3 === pendingStart || day_3 === range_2?.startDate) return firstDayMarker[locale];
    if (day_3 === range_2?.endDate) return lastDayMarker[locale];
    if (range_2 !== undefined) return inRangeMarker[locale];
    return undefined;
  };
  const lengthLimits = [`${atLeastText[locale]} ${formatDayCount(selectable.minDays, locale)}`, ...(selectable.maxDays === undefined ? [] : [`${atMostText[locale]} ${formatDayCount(selectable.maxDays, locale)}`])].join(', ');
  const phaseDate: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit'
  };
  return <div className="flex flex-col gap-4">
      {phases.length > 0 && <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
          {phases.map(phase => <li key={phase.key} className="flex items-center gap-1.5">
              <span aria-hidden="true" className={cn('h-1 w-4 rounded-full', phaseColor[phase.key])} />
              <span className="font-medium text-gray-900">{phase.label[locale]}</span>
              <span>
                {formatIsoDay(phase.startDate, locale, phaseDate)} –{' '}
                {formatIsoDay(phase.endDate, locale, phaseDate)}
              </span>
            </li>)}
        </ul>}
      <div role="group" aria-label={label} className="grid grid-cols-1 gap-6 @xl:grid-cols-2">
        {months.map(calendarMonth => <div key={calendarMonth.key}>
            <p className="font-heading mb-2 text-sm font-bold text-gray-900">
              {formatIsoDay(calendarMonth.days[0] ?? '', locale, {
            month: 'long',
            year: 'numeric'
          })}
            </p>
            <div className="grid grid-cols-7 gap-y-1">
              {weekdays.map(weekday => <span key={weekday} aria-hidden="true" className="pb-1 text-center text-xs font-medium text-gray-400">
                  {weekday}
                </span>)}
              {Array.from({
            length: calendarMonth.leadingBlanks
          }, (__0, blankIndex) => <span key={`blank-${blankIndex}`} aria-hidden="true" />)}
              {calendarMonth.days.map(day_4 => {
            const range_3 = findRange(day_4);
            const isEndpoint = day_4 === pendingStart || day_4 === range_3?.startDate || day_4 === range_3?.endDate;
            const isInRange = range_3 !== undefined;
            const isEnabled = isSelectable(day_4);
            const marker = getDayMarker(day_4);
            const phase_0 = findCampPhase(day_4);
            const dayName = [formatIsoDay(day_4, locale, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }), phase_0?.label[locale]].filter(Boolean).join(', ');
            return <div key={day_4} className="flex flex-col gap-0.5">
                    <button type="button" disabled={!isEnabled} aria-label={marker === undefined ? dayName : `${dayName}, ${marker}`} onClick={() => handleDayClick(day_4)} className={cn('flex h-10 items-center justify-center text-sm tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-1', {
                'rounded-md bg-green-600 font-bold text-white': isEndpoint,
                'bg-green-100 text-green-900': isInRange && !isEndpoint,
                'cursor-pointer rounded-md text-gray-900 hover:bg-gray-100': isEnabled && !isEndpoint && !isInRange,
                'cursor-not-allowed text-gray-300': !isEnabled && !isEndpoint && !isInRange
              })}>
                      {Number(day_4.slice(8))}
                    </button>
                    {/* One continuous bar per phase along each week row, rounded where it begins and ends. */}
                    <span aria-hidden="true" className={cn('h-1', phase_0 !== undefined && phaseColor[phase_0.key], {
                'rounded-l-full': day_4 === phase_0?.startDate,
                'rounded-r-full': day_4 === phase_0?.endDate,
                'opacity-40': !isEnabled && !isEndpoint && !isInRange
              })} />
                  </div>;
          })}
            </div>
          </div>)}
      </div>

      <div aria-live="polite" className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-700">
        {pendingStart !== undefined && <p>
            {pickLastDayText[locale]} ({lengthLimits}).
          </p>}
        {selectedRanges.map(range_4 => {
        const rangeText = formatRangeDates(range_4, locale);
        return <div key={range_4.startDate} className="flex items-center justify-between gap-2">
              <p className="font-bold text-gray-900">{formatRange(range_4, locale)}</p>
              <button type="button" aria-label={`${removeRangeText[locale]}: ${rangeText}`} onClick={() => removeRange(range_4)} className="cursor-pointer rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>;
      })}
        {pendingStart === undefined && selectedRanges.length > 0 && <p className="mt-1 text-xs text-gray-500">
            {isAtLimit ? pickAgainText[locale] : addAnotherRangeText[locale]}
          </p>}
        {pendingStart === undefined && selectedRanges.length === 0 && <p>
            {pickFirstDayText[locale]} ({lengthLimits})
          </p>}
      </div>
    </div>;
};

/**
 * Lets a helper who cannot commit to a whole camp phase mark the consecutive days they can
 * help on instead of a concrete job, optionally together with the Ressort they would like to
 * help in.
 *
 * The block renders two form fields: the day range under `name`, and — when the editor filled
 * in `ressortName` — the Ressort wish under that second name. Anything reading a form
 * definition field by field (step validation, initial state, server-side validation) has
 * to account for that second name explicitly.
 */
export const DateSlotSelection: React.FC<DateSlotSelectionProperties> = t0 => {
  const $ = _c(36);
  const {
    control,
    name,
    label,
    required,
    startDate,
    endDate,
    minDays,
    maxDays,
    maxRanges,
    ressortName,
    ressortLabel,
    ressortRequired
  } = t0;
  const locale = (useCurrentLocale(i18nConfig) ?? "de") as Locale;
  let t1;
  let t2;
  if ($[0] !== endDate || $[1] !== maxDays || $[2] !== maxRanges || $[3] !== minDays || $[4] !== startDate) {
    t2 = getSelectableDays({
      startDate,
      endDate,
      minDays,
      maxDays,
      maxRanges
    });
    $[0] = endDate;
    $[1] = maxDays;
    $[2] = maxRanges;
    $[3] = minDays;
    $[4] = startDate;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  t1 = t2;
  const selectable = t1;
  let t3;
  let t4;
  if ($[6] !== locale) {
    t4 = RESSORT_WISH_OPTIONS.map(option => ({
      value: option.value,
      label: option.label[locale]
    }));
    $[6] = locale;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  t3 = t4;
  const ressortOptions = t3;
  const asksForRessort = typeof ressortName === "string" && ressortName !== "";
  let t5;
  if ($[8] !== required) {
    t5 = Boolean(required) && <Required />;
    $[8] = required;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== label || $[11] !== t5) {
    t6 = <label className="font-body mb-4 block text-sm font-bold text-gray-900">{label}{t5}</label>;
    $[10] = label;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = required === true ? requiredFieldMessage[locale] : false;
  let t8;
  if ($[13] !== t7) {
    t8 = {
      required: t7
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== label || $[16] !== locale || $[17] !== selectable) {
    t9 = t10 => {
      const {
        field: t11,
        fieldState: t12
      } = t10;
      const {
        onChange,
        value
      } = t11;
      const {
        error
      } = t12;
      return <div className="flex flex-col gap-4">{selectable === undefined ? <div className="flex flex-col items-center justify-center py-12 text-center"><div className="mb-4 rounded-full bg-gray-50 p-6"><CalendarX className="h-12 w-12 text-gray-400" /></div><p className="max-w-[280px] text-sm text-gray-500">{noDaysText[locale]}</p></div> : <DayRangeCalendar selectable={selectable} value={value as unknown} onChange={nextValue => (onChange as (value: string) => void)(nextValue)} label={label} locale={locale} />}{error && <p className="mt-1 text-xs text-red-600">{error.message}</p>}</div>;
    };
    $[15] = label;
    $[16] = locale;
    $[17] = selectable;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== control || $[20] !== name || $[21] !== t8 || $[22] !== t9) {
    t10 = <Controller control={control} name={name} rules={t8} render={t9} />;
    $[19] = control;
    $[20] = name;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== asksForRessort || $[25] !== control || $[26] !== locale || $[27] !== ressortLabel || $[28] !== ressortName || $[29] !== ressortOptions || $[30] !== ressortRequired) {
    t11 = asksForRessort && <div className="mt-6"><label className="font-body mb-1 block text-sm font-medium text-gray-500" htmlFor={ressortName}>{ressortLabel}{ressortRequired === true && <Required />}</label><Controller control={control} name={ressortName} defaultValue="" rules={{
        required: ressortRequired === true ? requiredFieldMessage[locale] : false
      }} render={t12 => {
        const {
          field: t13,
          fieldState: t14
        } = t12;
        const {
          onChange: onChange_0,
          value: value_0,
          ref
        } = t13;
        const {
          error: error_0
        } = t14;
        return <><select id={ressortName} ref={ref} value={typeof value_0 === "string" ? value_0 : ""} onChange={event => onChange_0(event.target.value)} className={cn("font-body min-h-10 w-full cursor-pointer rounded-md border-0 px-3 py-2 text-base text-gray-600 shadow-sm ring-1 transition-all duration-200 ring-inset", error_0 ? "bg-red-50 ring-red-500" : "bg-green-100 ring-transparent hover:ring-green-600 focus:ring-2 focus:ring-green-600")}><option value="" disabled={true}>{ressortPlaceholder[locale]}</option>{ressortOptions.map(_temp4)}</select>{error_0 && <p className="mt-1 text-xs text-red-600">{error_0.message}</p>}</>;
      }} /></div>;
    $[24] = asksForRessort;
    $[25] = control;
    $[26] = locale;
    $[27] = ressortLabel;
    $[28] = ressortName;
    $[29] = ressortOptions;
    $[30] = ressortRequired;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t6) {
    t12 = <div className="@container mb-4 w-full">{t6}{t10}{t11}</div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t6;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
};
function _temp4(option_0) {
  return <option key={option_0.value} value={option_0.value}>{option_0.label}</option>;
}